// components imports
import { FC, useContext } from 'react'
import { Box, Grid, Typography } from '@mui/material'
import BillingCyclesList from './BillingCyclesList'
import { CustomController } from '../../../common/CustomController'
import FlatFeeSpecialPaymentsForm from './FlatFeeSpecialPaymentsForm'

// others
import palette from '../../../../theme/palette'
import { InfoOutlined } from '@mui/icons-material'
import { CustomDatePickerController } from '../../../common/CustomDatePickerRController'
import { CaseContext } from '../../../../context/CaseContext'
import { useGetInitialPaymentLockQuery } from '../../../../../generated'

const InfoTooltip = ({ info }: { info: string }) => (
  <Box display="flex" gap={1} pt={1}>
    <InfoOutlined sx={{ fontSize: '16px', color: '#000000' }} />
    <Typography variant="caption" color="#000000">
      {info}
    </Typography>
  </Box>
)

interface FlatFeeBillingFormProps {
  isEdit?: boolean
}

const FlatFeeBillingForm: FC<FlatFeeBillingFormProps> = ({ isEdit = false }) => {
  const { currentCase } = useContext(CaseContext)
  const caseId = currentCase?.id

  // Edit only: the Initial Payment locks once a live bill carries it. The server enforces the same rule.
  const { data, loading } = useGetInitialPaymentLockQuery({
    variables: { caseId: caseId || '' },
    skip: !isEdit || !caseId,
    fetchPolicy: 'network-only'
  })
  const lock = data?.getInitialPaymentLock
  const isUpfrontLocked = isEdit && (loading || lock?.canEdit === false)

  return (
    <Grid container item mt={1.5}>
      <Grid item xs={12} md={6} pr={{ md: 3 }} sx={{ borderRight: { md: 1 }, borderColor: palette.lightGrayThree }}>
        <Typography variant="h3" textTransform="uppercase" fontSize={16} fontWeight={700}>
          Flat Fee
        </Typography>
        <InfoTooltip info="Total Payment of the case" />

        <Box mt={2.5} display="flex" flexDirection="column" gap={2.5}>
          <CustomController
            fieldType="number"
            controllerName="billing.flatFee"
            controllerLabel="Total Flat Fee ($)"
            dollarAdornment
            sx={{ my: 0 }}
          />
        </Box>
        <Box mt={2.5}>
          <CustomDatePickerController
            controllerName="billing.pauseBillingUntil"
            controllerLabel="Billing Paused Until"
            sx={{ my: 0 }}
          />
          <InfoTooltip info="Billing paused means that the monthly retainer amount and special payments (if any) will not be charged until the date (if selected) in above field. However, all the expense entries, cost entries or other legal fee entries will still be part of the monthly bill." />
        </Box>
      </Grid>

      <Grid item xs={12} md={6} pl={{ md: 3 }} mt={{ xs: 3, md: 0 }}>
        <Typography variant="h3" textTransform={'uppercase'} fontSize={16} fontWeight={700}>
          Payment Plan
        </Typography>
        <InfoTooltip info="Total fee will be divided into monthly payments" />

        <Box mt={2.5} display="flex" flexDirection="column" gap={2.5}>
          <Box>
            <CustomController
              fieldType="number"
              controllerName="billing.upfrontFlatFee"
              controllerLabel="Upfront Amount ($)"
              dollarAdornment
              isDisabled={isUpfrontLocked}
              sx={{ my: 0 }}
            />
            {/* Server text as is, so it matches what a rejected save says. */}
            {lock?.canEdit === false && lock.message && <InfoTooltip info={lock.message} />}
          </Box>
          <CustomDatePickerController
            controllerName="billing.paymentPlanStartDate"
            controllerLabel="Monthly Payments Start Date"
            sx={{ my: 0 }}
          />
          <CustomController
            fieldType="number"
            controllerName="billing.monthlyFlatFee"
            controllerLabel="Monthly Payment ($)"
            dollarAdornment
            sx={{ my: 0 }}
          />
          <BillingCyclesList name="billing.billingCycle" title="Billing Cycle" />
        </Box>
      </Grid>

      <Grid item xs={12} mt={2.5}>
        <FlatFeeSpecialPaymentsForm />
      </Grid>
    </Grid>
  )
}

export default FlatFeeBillingForm
